import { useQuery } from '@apollo/client';
import { formatDistanceToNow } from 'date-fns';
import React, { FC, useCallback, useMemo } from 'react';
import Avatar from '@atlaskit/avatar';
import Tooltip from '@atlaskit/tooltip';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { ApprovalStatus, FetchTaskActivityLogQuery, TaskAuditTrail, TaskAuditTrailContext } from '@css-xp/apd-common';
import { ErrorComponents, FormattedDate } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { fetchTaskActivityLog } from '../../../queries/tasks';
import './activity-log-tab.scss';

interface RequestProps {
  taskId: string;
}

export const ActivityLogTab: FC<RequestProps> = ({ taskId }) => {
  const userProfile = useApplicationContext();

  const { data, loading, error } = useQuery<FetchTaskActivityLogQuery>(fetchTaskActivityLog, {
    skip: !userProfile?.userId,
    variables: {
      taskId: taskId,
      id: userProfile?.userId?.split('@')[0],
    },
  });

  const taskAuditTrail = useMemo(() => data?.taskActivityLog?.[0]?.taskAuditTrail, [data, taskId]);

  const getMessage = useCallback(
    (log: TaskAuditTrail) => {
      let msg = '';
      let comment = '';
      if (log?.context === TaskAuditTrailContext.COMMENT) {
        msg = `commented with `;
        comment = log?.value || '';
      } else if (log?.context === TaskAuditTrailContext.INITIATED) {
        msg = 'initiated the request';
      } else if (log?.context === TaskAuditTrailContext.STATUS_CHANGE) {
        switch (log?.value) {
          case ApprovalStatus.WAITING_FOR_REVIEW:
            msg = 'unassigned the request';
            break;
          case ApprovalStatus.IN_REVIEW:
            msg = 'is the assignee of the request';
            break;
          case ApprovalStatus.APPROVED:
            msg = 'approved the request';
            break;
          case ApprovalStatus.DECLINED:
            msg = 'declined the request';
            break;
          case ApprovalStatus.REASONS_REQUESTED:
            msg = 'requested supporting reasons';
            break;
          case ApprovalStatus.REASONS_PROVIDED:
            msg = 'replied with supporting reasons';
            break;
          case ApprovalStatus.REQUEST_CANCELED:
            msg = 'cancelled the request';
            break;
        }
      }
      return (
        <>
          <span className="text-bold">{`${log?.actor} `}</span>
          {msg}
          <i>{comment}</i>
        </>
      );
    },
    [taskId]
  );

  const visibleLogs = useMemo(() => {
    return taskAuditTrail?.slice()?.sort((c1, c2) => {
      const c1Time = c1?.time ? new Date(c1.time).getTime() : 0;
      const c2Time = c2?.time ? new Date(c2.time).getTime() : 0;
      return c2Time - c1Time;
    });
  }, [taskAuditTrail]);

  if (loading) return <LoadingIndicator />;

  if (error) {
    return <ErrorComponents.ErrorPanel error={new Error(error.message)} />;
  }

  return (
    <div className="activity-log-tab">
      <div className="activity-log-grid" data-testid="activity-log-grid">
        {visibleLogs?.map((taskAudit, index) => {
          const time = taskAudit?.time ? new Date(taskAudit.time).getTime() : 0;
          return (
            taskAudit?.actor && (
              <div className="task-log-wrapper" key={index}>
                <Avatar src={undefined} size="medium" />
                <div className="task-log">
                  <p>{getMessage(taskAudit)}</p>
                  <Tooltip content={<FormattedDate date={taskAudit?.time ?? null} showTime />}>
                    <p>{formatDistanceToNow(time, { addSuffix: true })}</p>
                  </Tooltip>
                </div>
              </div>
            )
          );
        })}
      </div>
    </div>
  );
};
